import type { Project } from '../types'
import { assetOf } from './model'
import { effectiveSubStyle, textBlock } from './textLayout'

export interface ExportOptions {
  shortSide: number // 720 | 1080 | 1440 | 2160
  fps: number
  quality: 'normal' | 'high' | 'max'
  filename: string
}

/** Output size keeping the project aspect; short side = chosen resolution. */
export function outputSize(p: Project, shortSide: number): { width: number; height: number } {
  const { width: W, height: H } = p.settings
  const even = (n: number) => Math.max(2, Math.round(n / 2) * 2)
  if (W >= H) return { width: even((shortSide * W) / H), height: even(shortSide) }
  return { width: even(shortSide), height: even((shortSide * H) / W) }
}

function bitrateK(width: number, height: number, fps: number, q: ExportOptions['quality']): number {
  // ~0.1 bits/pixel/frame for "normal", scaled up for higher quality
  const bpp = q === 'max' ? 0.2 : q === 'high' ? 0.14 : 0.1
  return Math.round((width * height * fps * bpp) / 1000)
}

export function buildExportRequest(p: Project, o: ExportOptions) {
  const { width, height } = outputSize(p, o.shortSide)
  const visual = p.tracks.filter((t) => t.kind === 'video')
  const trackNo = new Map<string, number>()
  visual.forEach((t, i) => trackNo.set(t.id, visual.length - 1 - i)) // bottom layer = 0
  p.tracks.filter((t) => t.kind === 'audio').forEach((t, i) => trackNo.set(t.id, visual.length + i))
  const trackById = new Map(p.tracks.map((t) => [t.id, t]))

  const clips = []
  for (const c of p.clips) {
    const tr = trackById.get(c.trackId)
    if (!tr) continue
    const base = {
      sourceInSec: c.sourceInSec,
      sourceOutSec: c.sourceOutSec,
      timelineInSec: c.startSec,
      track: trackNo.get(c.trackId) ?? 0,
      volume: c.audio.volume,
      speed: c.speed,
      muted: c.audio.muted || tr.muted,
      opacity: tr.visible ? c.transform.opacity : 0,
      scale: c.transform.scale,
      xOffset: c.transform.x,
      yOffset: c.transform.y,
    }
    if (c.kind === 'text' || c.kind === 'subtitle') {
      if (!tr.visible) continue
      const b = textBlock(p, c)
      if (!b) continue
      const wrapped = b.lines.join('\n')
      if (c.kind === 'text') {
        clips.push({
          ...base, kind: 'text', sourcePath: '', durationSec: c.durationSec, muted: true,
          text: { ...c.text!, text: wrapped },
        })
      } else {
        clips.push({
          ...base, kind: 'subtitle', sourcePath: '', durationSec: c.durationSec, muted: true,
          rawText: wrapped, subStyle: effectiveSubStyle(p, c), positionMode: c.positionMode ?? 'preset',
        })
      }
      continue
    }
    const a = assetOf(p, c)
    if (!a) continue
    clips.push({
      ...base,
      sourcePath: a.info.path,
      sourceOutSec: c.kind === 'image' ? c.durationSec : c.sourceOutSec,
      sourceInSec: c.kind === 'image' ? 0 : c.sourceInSec,
      speed: c.kind === 'image' ? 1 : c.speed,
      rotationDeg: c.transform.rotation,
      color: c.color,
      fadeInSec: c.audio.fadeInSec,
      fadeOutSec: c.audio.fadeOutSec,
    })
  }

  const safeName = (o.filename || p.name || 'video').replace(/[^\p{L}\p{N} ._-]+/gu, '_').trim() || 'video'
  return {
    outputFilename: `${safeName}.mp4`,
    width,
    height,
    fps: o.fps,
    videoBitrateK: bitrateK(width, height, o.fps, o.quality),
    audioBitrateK: o.quality === 'normal' ? 160 : 256,
    encoder: 'auto',
    clips,
    totalDurationSec: p.durationSec,
    backgroundColor: p.settings.backgroundColor || '#000000',
    projectWidth: p.settings.width,
    projectHeight: p.settings.height,
  }
}
